import { z } from 'zod';

import type { RotateCredentialsInput } from './queries';

import { FormDialog } from '@/components/form-dialog';
import { FormField } from '@/components/form-field';

// Phase 8.5-04 SEC-02 — admin-only credentials rotation form (ADR-014).
// Mirrors backend `RotateCredentialsSchema`. Steam nomenclature:
// `password` is the rotatable login credential (not the immutable steamLogin).
// `revocationCode` is optional/nullable per ADR-009 minimal-format imports.
const RevocationCodeRegex = /^R\d{3,}$/i;

const FormSchema = z.object({
  password: z.string().min(1, 'Введите пароль').max(512),
  sharedSecret: z.string().min(1, 'Введите shared_secret').max(512),
  identitySecret: z.string().min(1, 'Введите identity_secret').max(512),
  revocationCode: z
    .string()
    .max(64)
    .optional()
    .refine(
      (v) => v === undefined || v === '' || RevocationCodeRegex.test(v),
      'revocation_code должен быть в формате /^R\\d{3,}$/',
    ),
});

type FormValues = z.infer<typeof FormSchema>;

const defaultValues: FormValues = {
  password: '',
  sharedSecret: '',
  identitySecret: '',
  revocationCode: '',
};

export interface CredentialsRotateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steamLogin: string;
  isPending: boolean;
  submitError?: string;
  onSubmit: (payload: RotateCredentialsInput) => void;
}

export const CredentialsRotateDialog = ({
  open,
  onOpenChange,
  steamLogin,
  isPending,
  submitError,
  onSubmit,
}: CredentialsRotateDialogProps) => {
  const handleSubmit = (values: FormValues): void => {
    const payload: RotateCredentialsInput = {
      password: values.password,
      sharedSecret: values.sharedSecret,
      identitySecret: values.identitySecret,
    };
    if (values.revocationCode && values.revocationCode !== '') {
      payload.revocationCode = values.revocationCode;
    }
    onSubmit(payload);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Ротация credentials"
      description={`Новые значения для ${steamLogin}. Сервер re-encrypt'ит каждое поле текущим key version. Audit-лог содержит только список ротированных полей.`}
      schema={FormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isPending={isPending}
      submitLabel="Ротировать"
      {...(submitError !== undefined ? { submitError } : {})}
    >
      <FormField<FormValues>
        name="password"
        label="Password"
        type="password"
        autoComplete="off"
        spellCheck={false}
      />
      <FormField<FormValues>
        name="sharedSecret"
        label="shared_secret"
        autoComplete="off"
        spellCheck={false}
      />
      <FormField<FormValues>
        name="identitySecret"
        label="identity_secret"
        autoComplete="off"
        spellCheck={false}
      />
      <FormField<FormValues>
        name="revocationCode"
        label="revocation_code"
        description="Опционально. Формат R\d{3,}"
        autoComplete="off"
        spellCheck={false}
      />
    </FormDialog>
  );
};
